import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Newspaper, RotateCw, Sparkles } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import WimArticleCard from "../components/shared/WimArticleCard";
import ArticleSearchBar from "../components/shared/ArticleSearchBar";
import SEOHead from "../components/shared/SEOHead";
import { api } from "../lib/api";
import { BLOG_CATEGORIES as CATEGORIES } from "../lib/articleCategories";
import { articleMatchesSearch } from "../lib/articleSearch";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("loading");
  const [nonce, setNonce] = useState(0);
  const nav = useNavigate();
  const searchActive = search.trim().length > 0;
  const searchResults = searchActive
    ? posts.filter((p) => articleMatchesSearch(p, search, ["title", "excerpt", "category"]))
    : [];
  const clearSearchAndFilters = () => {
    setSearch("");
    setCat("All");
  };

  const fetchPosts = useCallback((signal) => {
    if (cat === "Care News & Policy Watch") return Promise.resolve();
    setStatus("loading");
    const params = cat === "All" ? {} : { category: cat };
    return api
      .get("/api/blog", { params, signal })
      .then((r) => {
        setPosts(r.data.posts || []);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setStatus("error");
      });
  }, [cat]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPosts(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchPosts, nonce]);

  // bfcache-restore re-fetch so the grid never lingers in an empty/stale state
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setNonce((n) => n + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const onCategory = (c) => {
    if (c === "Care News & Policy Watch") {
      nav("/care-news");
      return;
    }
    setCat(c);
  };

  // Featured row supports either ONE hero card (legacy behaviour) or TWO
  // side-by-side pinned cards. Anything beyond two falls through into the
  // standard grid.
  const featuredList = posts.filter((p) => p.is_featured).slice(0, 2);
  const pinnedSlugs = new Set(featuredList.map((p) => p.slug));
  let heroFeatured = null;
  let dualPinned = null;
  let rest = posts;
  if (featuredList.length >= 2) {
    dualPinned = featuredList;
    rest = posts.filter((p) => !pinnedSlugs.has(p.slug));
  } else if (posts.length) {
    heroFeatured = posts[0];
    rest = posts.slice(1);
  }

  return (
    <div data-testid="page-blog">
      <SEOHead
        title="WIM Blog — Why It Matters articles for carers and families"
        description="Evergreen guidance, practical explainers and support resources for independent carers and families. The WIM Blog is the Just Care Community archive of articles that don't go out of date."
        path="/wim-blog"
      />
      <PageHero
        eyebrow="The WIM Blog"
        title="Why It Matters — in plain English."
        description="Evergreen guidance, practical explainers and support resources for carers and families. The WIM Blog is the JCC's home for the lessons that don't go out of date."
      />
      <section className="section">
        <div className="container-jcc">
          <ArticleSearchBar value={search} onChange={setSearch} testId="blog-search" />
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => onCategory(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/[ &]+/g, "-")}`}
                className={`pill px-3 py-1.5 ${
                  cat === c ? "bg-ink text-white" : "bg-slate-100 text-ink-muted hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Featured article (first item) — keeps existing visual treatment */}
          {status === "loading" && posts.length === 0 && (
            <div className="py-20 text-center text-ink-muted" data-testid="blog-loading">
              Loading articles…
            </div>
          )}

          {status === "error" && (
            <div
              className="card-base p-8 text-center max-w-xl mx-auto"
              data-testid="blog-error"
            >
              <h3 className="font-display text-xl font-semibold mb-2">We couldn't load articles</h3>
              <p className="text-ink-muted text-sm mb-5">
                Something went wrong loading the WIM Blog. Please try again — your connection may have dropped.
              </p>
              <button
                type="button"
                onClick={() => setNonce((n) => n + 1)}
                className="btn-teal inline-flex"
                data-testid="blog-retry"
              >
                <RotateCw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {status === "ok" && posts.length === 0 && (
            <div className="card-base p-8 text-center max-w-xl mx-auto" data-testid="blog-empty">
              <h3 className="font-display text-xl font-semibold mb-2">Nothing in this category yet</h3>
              <p className="text-ink-muted text-sm">
                We're still publishing into this category. Try{" "}
                <button
                  type="button"
                  onClick={() => setCat("All")}
                  className="text-teal hover:underline font-medium"
                >
                  all articles
                </button>{" "}
                in the meantime.
              </p>
            </div>
          )}

          {status === "ok" && posts.length > 0 && searchActive && searchResults.length === 0 && (
            <div className="card-base p-8 text-center max-w-xl mx-auto" data-testid="blog-search-empty">
              <h3 className="font-display text-xl font-semibold mb-2">No articles found</h3>
              <p className="text-ink-muted text-sm mb-5">
                No articles match "{search}" in this category.
              </p>
              <button
                type="button"
                onClick={clearSearchAndFilters}
                className="btn-outline inline-flex"
                data-testid="blog-clear-search"
              >
                Clear search and filters
              </button>
            </div>
          )}

          {status === "ok" && posts.length > 0 && searchActive && searchResults.length > 0 && (
            <>
              <p className="text-sm text-ink-soft mb-4" data-testid="blog-search-results-count">
                {searchResults.length} article{searchResults.length === 1 ? "" : "s"} found
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-search-results">
                {searchResults.map((p) => (
                  <WimArticleCard key={p.slug} post={p} />
                ))}
              </div>
            </>
          )}

          {status === "ok" && posts.length > 0 && !searchActive && (
            <>
              {/* Featured row — single hero OR dual pinned cards */}
              {dualPinned && (
                <div className="grid md:grid-cols-2 gap-6 mb-12" data-testid="blog-dual-featured">
                  {dualPinned.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/wim/${p.slug}`}
                      className="block group relative card-base p-6 ring-2 ring-purple/30"
                      data-testid={`blog-featured-card-${p.slug}`}
                    >
                      <span className="absolute -top-3 left-6 pill bg-purple text-white shadow-sm flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Editor's Pick
                      </span>
                      {p.featured_image && (
                        <div className="h-40 sm:h-48 rounded-xl overflow-hidden mb-5">
                          <img src={p.featured_image} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="pill bg-teal-50 text-teal">{p.category}</span>
                        {p.read_minutes ? (
                          <span className="text-xs text-ink-soft flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {p.read_minutes} min read
                          </span>
                        ) : null}
                      </div>
                      <h2 className="font-display text-xl md:text-2xl font-bold leading-snug group-hover:text-teal transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-3 text-ink-muted text-sm">{p.excerpt}</p>
                    </Link>
                  ))}
                </div>
              )}

              {heroFeatured && (
                <Link to={`/wim/${heroFeatured.slug}`} className="block mb-12 group" data-testid="blog-featured">
                  <div className={`grid lg:grid-cols-5 gap-8 items-center card-base p-8 ${
                    heroFeatured.is_featured ? "ring-2 ring-purple/30 relative" : ""
                  }`}>
                    {heroFeatured.is_featured && (
                      <span
                        className="absolute -top-3 left-6 pill bg-purple text-white shadow-sm flex items-center gap-1.5"
                        data-testid="blog-featured-pill"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Editor's Pick
                      </span>
                    )}
                    {heroFeatured.featured_image && (
                      <div className="lg:col-span-2 h-48 sm:h-56 lg:h-64 rounded-xl overflow-hidden">
                        <img src={heroFeatured.featured_image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className={heroFeatured.featured_image ? "lg:col-span-3" : "lg:col-span-5"}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="pill bg-teal-50 text-teal">{heroFeatured.category}</span>
                        {heroFeatured.read_minutes ? (
                          <span className="text-xs text-ink-soft flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {heroFeatured.read_minutes} min read
                          </span>
                        ) : null}
                      </div>
                      <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight group-hover:text-teal transition-colors">
                        {heroFeatured.title}
                      </h2>
                      <p className="mt-3 text-ink-muted">{heroFeatured.excerpt}</p>
                    </div>
                  </div>
                </Link>
              )}

              {/* Standard article grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((p) => (
                  <WimArticleCard key={p.slug} post={p} />
                ))}
              </div>
            </>
          )}

          {/* Cross-link to Care News (always shown — original placement) */}
          <div
            className="mt-16 rounded-3xl border border-slate-200 bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid="wim-to-care-news"
          >
            <div className="max-w-2xl">
              <div className="overline text-teal mb-2">Looking for sector updates?</div>
              <h3 className="font-display text-2xl font-semibold leading-snug">
                Care News &amp; Policy Watch covers the changes that affect carers and families.
              </h3>
              <p className="mt-2 text-ink-muted text-sm">
                Policy, funding, regulation and employment-law updates — explained briefly, no spin.
              </p>
            </div>
            <Link to="/care-news" className="btn-outline shrink-0">
              <Newspaper className="w-4 h-4" /> Open Care News
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
